import { FC, RefObject } from 'react';
import { LoadingOutlined } from '@ant-design/icons';
import { Col, Row, Tag } from 'antd';
import { capitalCase } from 'change-case';
import { TbArrowUpRight, TbInputSearch } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { EmptySection, OverflowFadeScrollArea } from '@/shared';
import { Command, SetStateMethod } from '@/types';
import { ellipsisStyling } from '@/utilities';

import Right from '../../99-Right';

type CommandPaletteGroupRow = {
    label: string;
    type: 'group';
};

type CommandPaletteCommandRow = Command & {
    type: 'command';
};

type CommandPaletteResultRow = CommandPaletteCommandRow | CommandPaletteGroupRow;

interface CommandPaletteResultsProps {
    flatList: CommandPaletteResultRow[];
    groupToIconMap: Record<string, React.ReactNode>;
    itemRefs: RefObject<Map<number, HTMLDivElement | null>>;
    loading: boolean;
    query: string;
    selectedIndex: number;
    setOpen: SetStateMethod<boolean>;
    setQuery: SetStateMethod<string>;
    setSelectedIndex: SetStateMethod<number>;
}

const CommandPaletteResults: FC<CommandPaletteResultsProps> = ({
    flatList,
    groupToIconMap,
    itemRefs,
    loading,
    query,
    selectedIndex,
    setOpen,
    setQuery,
    setSelectedIndex,
}): JSX.Element => {
    const { token } = useAntDesignTheme();

    return (
        <Row style={{ minHeight: 16 * 32 }}>
            <Col
                aria-label='Command results'
                role='region'
                span={query !== '' ? 24 : 12}
                style={{ borderRight: `1px solid ${token.colorBorder}`, minWidth: 0 }}
            >
                <OverflowFadeScrollArea
                    aria-label='Command palette results'
                    containerStyle={{ maxHeight: 16 * 32 }}
                    surface='elevated'
                >
                    {loading && query.length > 0 && (
                        <div
                            style={{
                                padding: '2.5rem 1rem',
                                textAlign: 'center',
                                fontSize: 12,
                                color: token.colorTextSecondary,
                                borderBottom: `1px solid ${token.colorBorder}`,
                            }}
                        >
                            <LoadingOutlined spin style={{ marginRight: '0.5rem' }} />
                            Searching...
                        </div>
                    )}
                    {flatList.map((item, i) =>
                        item.type === 'group' ? (
                            <div
                                key={`group-${item.label}`}
                                style={{
                                    padding: '0.25rem 0.5rem',
                                    fontSize: '12px',
                                    color: token.colorText,
                                    background: token.colorBgBase,
                                    borderTop: i !== 0 ? `1px solid ${token.colorBorder}` : undefined,
                                    borderBottom: `1px solid ${token.colorBorder}`,
                                }}
                            >
                                <span style={{ color: token.colorTextTertiary }}>
                                    {groupToIconMap[item.label]}
                                    {item.label && capitalCase(item.label)}
                                </span>
                            </div>
                        ) : (
                            <div
                                ref={(el) => itemRefs.current?.set(i, el)}
                                key={item.id}
                                onMouseEnter={() => {
                                    if (!item?.disabled) {
                                        setSelectedIndex(i);
                                    }
                                }}
                                onClick={() => {
                                    if (item?.disabled) {
                                        return;
                                    }

                                    item.onSelect();
                                    setOpen(false);
                                    setQuery('');
                                }}
                                style={{
                                    padding: '0.75rem',
                                    background: selectedIndex === i ? token.colorBorder : '',
                                    cursor: item?.disabled ? 'not-allowed' : 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    scrollMarginTop: '1.75rem',
                                    opacity: item?.disabled ? 0.58 : 1,
                                }}
                            >
                                <span
                                    style={{
                                        ...ellipsisStyling,
                                        color: token.colorText,
                                        display: 'flex',
                                        alignItems: 'center',
                                        paddingBottom: '2px',
                                    }}
                                >
                                    {item.icon}
                                    {item.label}
                                    {item.labelSuffix}
                                    {item?.externalLink && (
                                        <TbArrowUpRight
                                            style={{
                                                verticalAlign: '-0.1rem',
                                                color: token.colorTextTertiary,
                                                marginLeft: '0.25rem',
                                            }}
                                        />
                                    )}
                                </span>
                                {item.shortcut && (
                                    <Tag
                                        style={{
                                            borderColor: 'transparent',
                                            marginRight: 0,
                                            color: token.colorTextSecondary,
                                        }}
                                    >
                                        {item?.disabled ? (item.disabledReason ?? 'Unavailable') : item.shortcut}
                                    </Tag>
                                )}
                                {!item.shortcut && item?.disabled && (
                                    <Tag
                                        style={{
                                            borderColor: 'transparent',
                                            marginRight: 0,
                                            color: token.colorTextSecondary,
                                        }}
                                    >
                                        {item.disabledReason ?? 'Unavailable'}
                                    </Tag>
                                )}
                            </div>
                        ),
                    )}
                    {!loading && flatList.length === 0 && (
                        <EmptySection
                            height={16 * 32}
                            icon={<TbInputSearch size='1.75rem' style={{ color: token.colorTextTertiary }} />}
                            label='No Results'
                            description='Refine your search or choose another tab in the command palette.'
                        />
                    )}
                </OverflowFadeScrollArea>
            </Col>
            <Col aria-label='Command context' role='complementary' span={query !== '' ? 0 : 12}>
                <Right />
            </Col>
        </Row>
    );
};

export default CommandPaletteResults;
